import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  X,
  Send,
  Plus,
  MessageSquare,
  Clock,
  ChevronRight,
  Loader2,
  AlertCircle,
  CheckCircle,
  Headphones,
  Shield,
  StopCircle,
} from 'lucide-react';
import { api } from '../../api/client';
import { formatDateTimeFull, parseUTC } from '../../utils/dateUtils';

interface SupportTicket {
  id: string;
  ticketNumber: string;
  subject: string;
  category: string;
  status: string;
  messageCount: number;
  unreadCount: number;
  createdAt: string;
  updatedAt: string;
}

interface WaWiSupportDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

interface ActiveSupportSession {
  id: string;
  superAdmin?: { name: string; email: string } | null;
  startedAt: string;
  expiresAt: string;
}

const API_BASE = import.meta.env.VITE_API_URL || '';
const ANHANG_MAX_BYTES = 10 * 1024 * 1024;
const ANHANG_MAX_ANZAHL = 5;
const ANHANG_ENDUNGEN = [
  '.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg',
  '.pdf', '.doc', '.docx', '.txt', '.csv',
  '.zip', '.xls', '.xlsx',
];

export default function WaWiSupportDrawer({ isOpen, onClose }: WaWiSupportDrawerProps) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'new' | 'tickets' | 'remote'>('new');
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [loading, setLoading] = useState(false);

  // New ticket form
  const [subject, setSubject] = useState('');
  const [category, setCategory] = useState('general');
  const [priority, setPriority] = useState('normal');
  const [message, setMessage] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [submitting, setSubmitting] = useState(false);
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // Remote support state
  const [remoteReason, setRemoteReason] = useState('');
  const [remoteDuration, setRemoteDuration] = useState<1 | 4 | 24>(1);
  const [requestingRemote, setRequestingRemote] = useState(false);
  const [activeSessions, setActiveSessions] = useState<ActiveSupportSession[]>([]);

  useEffect(() => {
    if (isOpen && activeTab === 'tickets') {
      loadTickets();
    }
    if (isOpen && activeTab === 'remote') {
      loadActiveSessions();
    }
  }, [isOpen, activeTab]);

  const loadActiveSessions = async () => {
    try {
      const response = await api.get<{ sessions: ActiveSupportSession[] }>(
        '/support-access/active-sessions',
      );
      setActiveSessions(response?.sessions ?? []);
    } catch (error) {
      console.error('Failed to load support sessions:', error);
    }
  };

  const handleRequestRemote = async () => {
    if (remoteReason.trim().length < 5) {
      setNotification({ message: 'Bitte beschreiben Sie das Problem (mind. 5 Zeichen)', type: 'error' });
      return;
    }
    setRequestingRemote(true);
    setNotification(null);
    try {
      const response = await api.post<{ success: boolean; message?: string; error?: string }>(
        '/support-access/request-from-tenant',
        {
          reason: remoteReason.trim(),
          securityLevel: 'direct',
          duration: remoteDuration,
        },
      );
      if (response?.success) {
        setNotification({
          message: response.message || 'Support-Zugriff angefordert — unser Team wird sich darum kümmern.',
          type: 'success',
        });
        setRemoteReason('');
        loadActiveSessions();
      } else {
        setNotification({ message: response?.error || t('common.error'), type: 'error' });
      }
    } catch (error: any) {
      setNotification({ message: error?.message || t('common.error'), type: 'error' });
    } finally {
      setRequestingRemote(false);
    }
  };

  const handleTerminateSession = async (sessionId: string) => {
    try {
      await api.post(`/support-access/session/${sessionId}/terminate`, {});
      setNotification({ message: 'Support-Session beendet', type: 'success' });
      loadActiveSessions();
    } catch (error: any) {
      setNotification({ message: error?.message || t('common.error'), type: 'error' });
    }
  };

  const loadTickets = async () => {
    setLoading(true);
    try {
      const response = await api.get('/support/tickets');
      if (response?.success) {
        setTickets(response.tickets);
      }
    } catch (error) {
      console.error('Failed to load tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!subject.trim() || !message.trim()) return;

    setSubmitting(true);
    try {
      const deviceInfo = {
        userAgent: navigator.userAgent,
        currentUrl: window.location.href,
        screenSize: `${window.screen.width}x${window.screen.height}`,
        language: navigator.language,
        timestamp: new Date().toISOString(),
      };

      const response = await api.post<{
        success?: boolean;
        error?: string;
        ticketId?: string;
        ticketNumber?: string;
        messageId?: string;
      }>('/support/tickets', {
        subject,
        category,
        priority,
        message,
        source: 'wawi',
        deviceInfo,
      });

      if (response?.success) {
        // Dateien an das NEUE Ticket (und dessen erste Nachricht) hochladen
        let fehlgeschlagen = 0;
        if (files.length > 0 && response.ticketId) {
          const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
          for (const file of files) {
            try {
              const fd = new FormData();
              fd.append('file', file);
              if (response.messageId) fd.append('messageId', response.messageId);
              const res = await fetch(`${API_BASE}/support/tickets/${response.ticketId}/attachments`, {
                method: 'POST',
                headers: token ? { Authorization: `Bearer ${token}` } : {},
                body: fd,
              });
              const erg = await res.json();
              if (!erg?.success) fehlgeschlagen++;
            } catch {
              fehlgeschlagen++;
            }
          }
        }
        if (fehlgeschlagen > 0) {
          setNotification({
            message: t('myPortal.support.drawer.attachmentsFailed', {
              count: fehlgeschlagen,
              defaultValue:
                fehlgeschlagen === 1
                  ? 'Ticket angelegt, 1 Anhang konnte nicht hochgeladen werden'
                  : `Ticket angelegt, ${fehlgeschlagen} Anhänge konnten nicht hochgeladen werden`,
            }),
            type: 'error',
          });
        } else {
          setNotification({
            message: t('myPortal.support.drawer.ticketCreated', { number: response.ticketNumber }),
            type: 'success',
          });
        }
        setFiles([]);
        setSubject('');
        setCategory('general');
        setPriority('normal');
        setMessage('');
      } else {
        setNotification({ message: response?.error || t('common.error'), type: 'error' });
      }
    } catch (error) {
      setNotification({ message: t('common.error'), type: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'resolved':
      case 'closed':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'waiting_customer':
        return <AlertCircle className="w-4 h-4 text-orange-500" />;
      default:
        return <Clock className="w-4 h-4 text-blue-500" />;
    }
  };

  const formatRelativeTime = (dateString: string) => {
    const date = parseUTC(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    return `${diffDays}d`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />

      {/* Drawer */}
      <div className="relative w-full max-w-md bg-white dark:bg-gray-800 shadow-xl flex flex-col animate-slide-in-right">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-primary-600" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {t('myPortal.support.drawer.title')}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 dark:border-gray-700">
          <button
            onClick={() => { setActiveTab('new'); setNotification(null); }}
            className={`flex-1 py-2.5 text-sm font-medium text-center transition-colors ${
              activeTab === 'new'
                ? 'text-primary-600 border-b-2 border-primary-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Plus className="w-4 h-4 inline mr-1" />
            {t('myPortal.support.drawer.newTicket')}
          </button>
          <button
            onClick={() => setActiveTab('tickets')}
            className={`flex-1 py-2.5 text-sm font-medium text-center transition-colors ${
              activeTab === 'tickets'
                ? 'text-primary-600 border-b-2 border-primary-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <MessageSquare className="w-4 h-4 inline mr-1" />
            {t('myPortal.support.drawer.myTickets')}
          </button>
          <button
            onClick={() => { setActiveTab('remote'); setNotification(null); }}
            className={`flex-1 py-2.5 text-sm font-medium text-center transition-colors ${
              activeTab === 'remote'
                ? 'text-primary-600 border-b-2 border-primary-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Headphones className="w-4 h-4 inline mr-1" />
            {t('myPortal.support.drawer.remoteSupport', { defaultValue: 'Fernwartung' })}
          </button>
        </div>

        {/* Notification */}
        {notification && (
          <div
            className={`mx-4 mt-3 px-3 py-2 rounded-lg text-sm flex items-center gap-2 ${
              notification.type === 'success'
                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
            }`}
          >
            {notification.type === 'success' ? (
              <CheckCircle className="w-4 h-4 flex-shrink-0" />
            ) : (
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
            )}
            <span>{notification.message}</span>
            <button onClick={() => setNotification(null)} className="ml-auto">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {activeTab === 'remote' ? (
            <div className="space-y-4">
              <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-900/40 rounded-lg">
                <Headphones className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-blue-900 dark:text-blue-200">
                  Erlauben Sie unserem Support-Team direkten Zugriff auf Ihre WaWi, um Probleme schneller zu lösen.
                </p>
              </div>

              {/* Problem description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Problembeschreibung *
                </label>
                <textarea
                  value={remoteReason}
                  onChange={(e) => setRemoteReason(e.target.value)}
                  placeholder="Beschreiben Sie kurz das Problem (mind. 5 Zeichen)..."
                  className="input w-full"
                  rows={4}
                />
              </div>

              {/* Duration */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Zugriffsdauer
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {([1, 4, 24] as const).map((h) => (
                    <button
                      key={h}
                      type="button"
                      onClick={() => setRemoteDuration(h)}
                      className={`py-2 rounded-lg border text-sm font-medium transition-colors ${
                        remoteDuration === h
                          ? 'bg-primary-600 text-white border-primary-600'
                          : 'bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                      }`}
                    >
                      {h === 1 ? '1 Stunde' : h === 4 ? '4 Stunden' : '24 Stunden'}
                    </button>
                  ))}
                </div>
              </div>

              {/* DSGVO note */}
              <div className="flex items-start gap-2 p-3 bg-gray-50 dark:bg-gray-700/40 border border-gray-200 dark:border-gray-600 rounded-lg">
                <Shield className="w-4 h-4 text-gray-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  Ihr Datenschutz ist uns wichtig. Der Support-Zugriff ist zeitlich begrenzt, kann jederzeit beendet werden und wird DSGVO-konform protokolliert.
                </p>
              </div>

              {/* Submit */}
              <button
                onClick={handleRequestRemote}
                disabled={requestingRemote || remoteReason.trim().length < 5}
                className="btn btn-primary w-full"
              >
                {requestingRemote ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Zugriff anfordern
              </button>

              {/* Active sessions */}
              {activeSessions.length > 0 && (
                <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Aktive Support-Sessions
                  </h3>
                  <div className="space-y-2">
                    {activeSessions.map((session) => (
                      <div
                        key={session.id}
                        className="flex items-center gap-2 p-3 border border-green-200 dark:border-green-900/40 bg-green-50 dark:bg-green-900/20 rounded-lg"
                      >
                        <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-gray-900 dark:text-white truncate">
                            {session.superAdmin?.name || 'BISpicy Support'}
                          </p>
                          <p className="text-[10px] text-gray-500">
                            Läuft bis {formatDateTimeFull(session.expiresAt)}
                          </p>
                        </div>
                        <button
                          onClick={() => handleTerminateSession(session.id)}
                          className="p-1 rounded text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30"
                          title="Session beenden"
                        >
                          <StopCircle className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : activeTab === 'new' ? (
            <div className="space-y-4">
              {/* Subject */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('myPortal.support.drawer.subject')} *
                </label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder={t('myPortal.support.drawer.subjectPlaceholder')}
                  className="input w-full"
                />
              </div>

              {/* Category + Priority */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('myPortal.support.drawer.category')}
                  </label>
                  <select value={category} onChange={(e) => {
                    const cat = e.target.value;
                    setCategory(cat);
                    const templates: Record<string, string> = {
                      bug_report: '## Was ist passiert?\n\n\n## Schritte zum Nachstellen\n1. \n2. \n3. \n\n## Erwartetes Verhalten\n\n\n## Tatsächliches Verhalten\n',
                      feature_request: '## Feature-Beschreibung\n\n\n## Anwendungsfall\n\n\n## Vorgeschlagene Umsetzung (optional)\n',
                    };
                    if (templates[cat] && !message.trim()) {
                      setMessage(templates[cat]);
                    }
                  }} className="input w-full">
                    <option value="general">{t('admin.support.category.general')}</option>
                    <option value="technical">{t('admin.support.category.technical')}</option>
                    <option value="billing">{t('admin.support.category.billing')}</option>
                    <option value="feature_request">{t('myPortal.support.drawer.featureRequest')}</option>
                    <option value="bug_report">{t('myPortal.support.drawer.bugReport')}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('myPortal.support.drawer.priority')}
                  </label>
                  <select value={priority} onChange={(e) => setPriority(e.target.value)} className="input w-full">
                    <option value="low">{t('admin.support.priority.low')}</option>
                    <option value="normal">{t('admin.support.priority.normal')}</option>
                    <option value="high">{t('admin.support.priority.high')}</option>
                    <option value="urgent">{t('admin.support.priority.urgent')}</option>
                  </select>
                </div>
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('myPortal.support.drawer.message')} *
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={t('myPortal.support.drawer.messagePlaceholder')}
                  className="input w-full"
                  rows={5}
                />
              </div>

              {/* Anhänge: nur clientseitig vorgehalten, Upload erst nach dem Anlegen an das NEUE Ticket (V-2821 F-002) */}
              <div className="space-y-1">
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  multiple
                  accept={ANHANG_ENDUNGEN.join(',')}
                  onChange={(e) => {
                    const neu = Array.from(e.target.files ?? []).filter(
                      (f) =>
                        f.size <= ANHANG_MAX_BYTES &&
                        ANHANG_ENDUNGEN.includes('.' + (f.name.split('.').pop() ?? '').toLowerCase())
                    );
                    setFiles((prev) => [...prev, ...neu].slice(0, ANHANG_MAX_ANZAHL));
                    e.target.value = '';
                  }}
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={files.length >= ANHANG_MAX_ANZAHL || submitting}
                  className="btn btn-secondary btn-sm"
                >
                  {t('myPortal.support.drawer.addAttachment', { defaultValue: 'Anhang hinzufügen' })}
                </button>
                {files.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {files.map((f, i) => (
                      <span key={`${f.name}-${i}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-xs">
                        {f.name}
                        <button type="button" onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))}>
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Submit */}
              <button
                onClick={handleSubmit}
                disabled={!subject.trim() || !message.trim() || submitting}
                className="btn btn-primary w-full"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                {t('myPortal.support.drawer.submit')}
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                </div>
              ) : tickets.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">{t('myPortal.support.drawer.noTickets')}</p>
                </div>
              ) : (
                tickets.map((ticket) => (
                  <div
                    key={ticket.id}
                    className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    {getStatusIcon(ticket.status)}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {ticket.subject}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <span className="font-mono">{ticket.ticketNumber}</span>
                        <span>&middot;</span>
                        <span>{formatRelativeTime(ticket.updatedAt)}</span>
                        {ticket.unreadCount > 0 && (
                          <span className="bg-primary-500 text-white rounded-full px-1.5 py-0.5 text-[10px] font-bold">
                            {ticket.unreadCount}
                          </span>
                        )}
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
